import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useApp } from '../context/AppContext';
import { T } from '../lib/i18n';
import useTts from '../lib/useTts';
import { Volume2, Mic, Square, RotateCcw, ChevronRight, ChevronLeft, Loader2, Star, Trophy, Play, BookOpen, Sparkles, Languages, Wand2, LogOut, Bot as BotIcon, Camera } from 'lucide-react';
import { toast } from 'sonner';
import DebugPinModal from '../components/DebugPinModal';
import MilestoneTester from '../components/MilestoneTester';
import ModerationFlagsCard from '../components/ModerationFlagsCard';
import SyllablePopover from '../components/SyllablePopover';
import AiDisclaimerBanner from '../components/AiDisclaimerBanner';
import AdminUsersCard from '../components/AdminUsersCard';
import AdminPlansCard from '../components/AdminPlansCard';
import AdminTestimonialsCard from '../components/AdminTestimonialsCard';
import AdminChangePinCard from '../components/AdminChangePinCard';
import { celebrateMilestone, stopCelebrationAudio } from '../lib/milestones';

// Session-language labels — read UI in session's language (not app UI language)
const RL = (lang) => (key) => (T[lang]?.[key] ?? T.en[key] ?? key);

// Iter-51: heading + highlighted-word markers live in `lib/textMarkers.js`
// so every preview surface (Home continue-reading card, BookOverview page
// thumbnails, Reading pane) uses the SAME strip logic — no more raw
// `## …` or `==…==` leaking into the UI.
import { HEADING_PREFIX, HIGHLIGHT_RE, stripMarkers, stripForSpeech, isNonSpeechPrefixToken, MATCH_MARK_OPEN, parseMatchExercise, matchLeftLabel, matchRightLabel } from '../lib/textMarkers';
// Iter-52.52 — Exercises live on their own /exercises/:book/:page
// screen (spec §5). Reading page now mounts a compact CTA card that
// navigates over and shows an "N questions" badge sourced from the
// same /exercises/detect endpoint (cheap after first call — the
// backend caches the result).
import { ExercisesPanel } from '../components/ExercisesPanel';
import { useNavigate as _useNav } from 'react-router-dom';

function ExercisesCta({ bookId, pageId, passage, language, matchBlock }) {
  const nav = _useNav();
  const [count, setCount] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post('/exercises/detect', {
          passage: passage || '',
          language: language || 'en',
          cache_key: `${bookId}:${pageId}:${language || 'en'}`,
          match_block: matchBlock || undefined,
        });
        if (!cancelled) setCount((data.exercises || []).length);
      } catch { if (!cancelled) setCount(0); }
    })();
    return () => { cancelled = true; };
  }, [bookId, pageId, language, passage, matchBlock]);

  // Hide entirely once we know there are zero exercises on this page.
  if (count === 0) return null;
  return (
    <button
      type="button"
      data-testid="exercises-open-cta"
      onClick={() => nav(`/exercises/${bookId}/${pageId}`)}
      className="w-full max-w-md mx-auto mt-4 mb-2 flex items-center justify-between gap-3 rounded-2xl bg-white border-2 border-[#B85582]/40 hover:border-[#B85582] p-3 md:p-4 shadow-sm"
    >
      <div className="flex items-center gap-2 text-left">
        <span className="text-2xl">📝</span>
        <div>
          <div className="text-sm md:text-base font-extrabold text-[#2B2140]">
            Exercises / अभ्यास
          </div>
          <div className="text-xs text-slate-500">
            {count == null ? 'Checking…' : `${count} question${count === 1 ? '' : 's'} — tap to open`}
          </div>
        </div>
      </div>
      <span className="text-[11px] font-extrabold text-[#B85582] uppercase tracking-wide">Open →</span>
    </button>
  );
}


// Client-side chunker — flattens paragraphs into a single sentence stream so
// paragraph breaks don't force a hard chunk boundary. Without this, a page
// with 11 short one-line paragraphs produced 11 chunks even in "Long" mode.
//
// Iter-51 augmentation: heading paragraphs (starting with `## `) are treated
// as their OWN unit and travel with the next body paragraph in the SAME
// chunk but on their own line — so the child still gets heading + intro
// paragraph together, just visually separated. Headings never trigger a
// hard chunk boundary by themselves (that would leave the child staring
// at a lone "Water Cycle:" chunk with no body to read).
function chunkText(text, maxWords) {
  if (!text) return [];
  const paras = text.trim().split(/\n\s*\n/).map(p => p.trim()).filter(Boolean);
  // Flatten paragraphs into an item stream where each item is either a
  // heading (indivisible, keeps `## ` prefix + its trailing colon), a
  // match_exercise sentinel paragraph (indivisible, rendered as its own
  // chunk so a single exercise never gets awkwardly split), or a
  // sentence. Track paragraph id so we can visually separate headings
  // from body when joining a chunk back together.
  const items = [];
  paras.forEach((para, pid) => {
    if (para.startsWith(MATCH_MARK_OPEN)) {
      // A match exercise is its OWN chunk — it doesn't participate in
      // the word-count packing loop below. Force a hard boundary before
      // and after by marking it as an indivisible atomic block.
      items.push({ text: para, isHeading: false, isMatch: true, pid });
    } else if (para.startsWith(HEADING_PREFIX)) {
      items.push({ text: para, isHeading: true, pid });
    } else {
      for (const s of para.split(/(?<=[.!?।])\s+/)) {
        const t = s.trim();
        if (t) items.push({ text: t, isHeading: false, pid });
      }
    }
  });
  const wcOf = (s) => (s.replace(HIGHLIGHT_RE, '$1').split(/\s+/).filter(Boolean).length || 0);
  const joinItems = (arr) => {
    let out = '';
    for (let i = 0; i < arr.length; i++) {
      const it = arr[i];
      if (i === 0) { out = it.text; continue; }
      const prev = arr[i - 1];
      // Heading and its body live on separate lines; different paragraphs
      // in the same chunk also get a line break so paragraph structure
      // survives the visual join.
      const sep = (prev.isHeading || it.isHeading || prev.pid !== it.pid) ? '\n' : ' ';
      out += sep + it.text;
    }
    return out;
  };
  const out = [];
  let cur = []; let wc = 0;
  for (const it of items) {
    if (it.isMatch) {
      // Flush the current chunk (if any) and emit the match as its own
      // standalone chunk.
      if (cur.length) { out.push(joinItems(cur)); cur = []; wc = 0; }
      out.push(it.text);
      continue;
    }
    const w = wcOf(it.text);
    if (cur.length && wc + w > maxWords && !cur[cur.length - 1].isHeading) {
      out.push(joinItems(cur));
      cur = [it]; wc = w;
    } else {
      cur.push(it); wc += w;
    }
  }
  if (cur.length) out.push(joinItems(cur));
  return out;
}
// Meaningful chunk sizes matched to Short = ~1-2 sentences, Medium = a
// paragraph, Long = multiple paragraphs / a full section.
const CHUNK_SIZES = { short: 18, medium: 45, long: 100 };

export default function Reading() {
  const { bookId, pageId } = useParams();
  const nav = useNavigate();
  const { activeChild, updateActiveChild } = useApp();
  const lang = 'placeholder'; // set after book loads
  const [book, setBook] = useState(null);
  const [pageIdx, setPageIdx] = useState(0);
  const [chunkIdx, setChunkIdx] = useState(0);
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [showResumeBanner, setShowResumeBanner] = useState(false);
  const [sessionSpeed, setSessionSpeed] = useState(activeChild?.tts_speed ?? 0.75);
  // Iter-50: word the child tapped to hear a syllable breakdown for.
  const [syllableWord, setSyllableWord] = useState(null);
  const [chunkSize, setChunkSize] = useState(activeChild?.chunk_size_preset ?? 'short');
  // Iter-51.99 — Read-to-Me karaoke highlight. Zero-based index into the
  // CURRENT chunk's word-token list (spaces & newlines don't count).
  // `-1` = no active word (idle, or brief 120 ms settle after audio ends).
  // Cleared when the chunk changes so a mid-narration navigation doesn't
  // leave a stale highlight on the new chunk.
  const [activeWordIdx, setActiveWordIdx] = useState(-1);
  // Iter-52.15 — end-of-library-passage handoff modal state. Declared
  // here (above the early-return `if (!book) return`) so React's Rules
  // of Hooks stays satisfied on every render.
  const [showLibraryHandoff, setShowLibraryHandoff] = useState(false);
  // Iter-52.19 — reference to the standalone purple Finish CTA so we
  // can pull it into view when it FIRST appears on the last chunk.
  // Without this the button rendered below-the-fold on desktop and
  // mobile alike (e.g. 9-chunk Independence Day passage), and parents
  // reported the handoff was "missing" when it was simply off-screen.
  const finishCtaRef = useRef(null);
  // Iter-51.99 — clear the karaoke highlight whenever the child
  // navigates to a different chunk (or page). Prevents a stale
  // active-word index from being applied to the freshly-rendered
  // chunk's token list, which would highlight the wrong word.
  useEffect(() => {
    setActiveWordIdx(-1);
    // Also stop any in-flight Read-to-Me audio so a chunk swap
    // doesn't leave the previous narration playing.
    try { tts.stop(); } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIdx, chunkIdx]);

  // Iter-52.19 — auto-scroll the Finish-passage CTA into view when
  // it first renders on the last chunk of a Starter Library passage.
  // Fixes reports that the handoff was "missing" on long passages —
  // in reality it was rendered but below the fold. `book.pages` may
  // be undefined during the initial load so we guard against that.
  useEffect(() => {
    if (!book?.is_library) return;
    const pages = book?.pages || [];
    if (pageIdx + 1 < pages.length) return;
    if (finishCtaRef.current) {
      // small delay so React finishes laying out the CTA + any
      // sibling result box that expanded on the same tick.
      const t = setTimeout(() => {
        try {
          finishCtaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch {}
      }, 200);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [book?.is_library, pageIdx, chunkIdx, book?.pages?.length]);
  const [myAudioUrl, setMyAudioUrl] = useState(null);
  const [meaningText, setMeaningText] = useState(null);
  const [meaningLoading, setMeaningLoading] = useState(false);
  const [summaryText, setSummaryText] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [coachWord, setCoachWord] = useState(null);
  const [coachStage, setCoachStage] = useState('idle');
  // Iter-51.3: per-chunk "first attempt" gate for the Read-to-me button.
  // Prevents the child from tapping Read-to-me BEFORE they've made their
  // own first attempt on a fresh chunk — which was effectively letting
  // them hear the correct pronunciation and mimic it back for a
  // successful score without actually decoding the text. Keyed by
  // "<page_id>::<chunk_index>" so every new page/chunk resets its own
  // gate; persisted for the lifetime of the session (a page revisit in
  // the same session doesn't re-gate — the child already tried it once).
  const [attemptedChunks, setAttemptedChunks] = useState(() => new Set());

  // Iter-51.6: "Done for Today" used to block for ~10 s (LLM + speak +
  // 5 s mic + ack) with NO visible feedback, then jump to /dashboard.
  // Now we render an INSTANT compact overlay the moment the button is
  // tapped — the child sees a farewell card with a live listening
  // indicator, while the audio + mic flow runs behind it. Snappier,
  // matches the requested compact layout.
  const [goodbyeState, setGoodbyeState] = useState('idle'); // idle|preparing|greeting|listening|closing
  const [goodbyeText, setGoodbyeText] = useState('');
  const [debugOpen, setDebugOpen] = useState(false);
  // Debug panel is invisible in normal use — no icon, no visible affordance.
  // Two-layer secret access: (a) 10 rapid taps on the visible "C N/N" chunk
  // indicator, then (b) the parent PIN.
  //
  // Reliability history / why THIS approach (iter-28):
  //   iter-25: visible bug icon                  — rejected as too obvious
  //   iter-26: invisible <button opacity-0>      — hit-testing failed on mobile
  //   iter-27: invisible <button bg-transparent> — still flaky (3rd regression)
  //   iter-28: piggyback on an ALREADY-RENDERED, ALREADY-TAPPABLE visible span
  //            ("C 1/11") which is guaranteed to receive taps. Kids won't
  //            tap the chunk indicator repeatedly by accident.
  //
  // We also give the parent subtle visual feedback (a small dot at tap #5)
  // so they know their taps are counting — no dot means the handler isn't
  // wired, so debugging future regressions is trivial.
  const [debugPinAsking, setDebugPinAsking] = useState(false);
  // Cached verified debug PIN — needed to call /api/debug/simulate-streak
  // from the MilestoneTester without re-prompting. Cleared when the debug
  // panel closes so it never lingers between session opens.
  const [debugPin, setDebugPin] = useState('');
  const [tapProgress, setTapProgress] = useState(0);   // 0 → hidden; 5-9 → dot
  const debugTapsRef = useRef({ count: 0, first: 0 });
  const openDebug = () => {
    // SECURITY: always show the PIN modal for debug regardless of whether
    // the parent already unlocked their PIN earlier this session (e.g. for
    // an OCR upload). The shared 10-min unlock token was the reason a child
    // could stumble into the debug panel after mom had used camera earlier:
    //   openDebug() saw getPinUnlockToken() truthy → skipped the modal.
    // Debug is diagnostic/high-trust and must never rely on cached auth.
    setDebugPinAsking(true);
  };
  const registerSecretTap = () => {
    const now = Date.now();
    const s = debugTapsRef.current;
    // 4 s streak window (a hair looser than iter-27's 3 s) — accommodates
    // slightly slower parent fingers while still ruling out accidental
    // triggers by a child scrolling.
    if (now - s.first > 4000) { s.count = 0; s.first = now; }
    s.count += 1;
    setTapProgress(s.count >= 5 && s.count < 10 ? s.count : 0);
    if (s.count >= 10) {
      s.count = 0;
      s.first = 0;
      setTapProgress(0);
      openDebug();
    }
  };
  const [lastTranscript, setLastTranscript] = useState('');
  const tts = useTts(sessionSpeed);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  // Iter-51.8 perf: keep the mic MediaStream alive across recordings so the
  // 2nd+ tap doesn't have to wait for `getUserMedia` again. Cold Chrome
  // spends 100-500 ms on this call, Safari sometimes 700 ms — that showed
  // up in production as "the mic button spins for 30 rotations before it
  // starts listening". We now open the stream once, reuse it forever, and
  // release it on unmount.
  const streamRef = useRef(null);
  // Abort controller for in-flight STT/session scoring — lets the child
  // start a NEW recording immediately if they realize the last read was
  // wrong, instead of watching a spinner for however long Deepgram +
  // /sessions takes.
  const scoreAbortRef = useRef(null);
  // Iter-51.9: PROD-VISIBLE latency breakdown. Third report of "mic is slow"
  // in prod with no way to know WHERE the slowness lives — upload, STT,
  // scoring, or something else. This state holds the last measured phase
  // timings and is rendered as a compact chip beside the mic when the
  // child is out of recording mode. It's also mirrored to
  // window.__micTimings + console.log('[mic-perf]') so a parent can share
  // hard numbers from readbuddy.co.in DevTools instead of "it feels slow".
  const [scorePhase, setScorePhase] = useState(''); // '' | 'uploading' | 'scoring' | 'saving'
  const [lastTimings, setLastTimings] = useState(null); // { blob_kb, stt_ms, sess_ms, total_ms }
  // Cleanup shared stream on unmount so the browser mic indicator turns
  // off when the child leaves the reading page.
  useEffect(() => () => {
    try { streamRef.current?.getTracks?.().forEach(tk => tk.stop()); } catch {}
    streamRef.current = null;
    try { scoreAbortRef.current?.abort?.(); } catch {}
  }, []);
  const getSharedStream = async () => {
    // Reuse the existing live stream if we already have one. A track can
    // end (e.g. the user switched Bluetooth headsets); if so, drop it and
    // request a fresh stream. Also handle the case where the stream was
    // aborted for any other reason.
    const existing = streamRef.current;
    if (existing && existing.getAudioTracks().some(tk => tk.readyState === 'live')) {
      return existing;
    }
    try { existing?.getTracks?.().forEach(tk => tk.stop()); } catch {}
    const s = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = s;
    return s;
  };

  useEffect(() => {
    // Iter-52.15 — Starter Library: passage ids begin with `lib_`. In
    // that case we synthesize a one-page "virtual book" from the
    // library endpoint so the rest of Reading.jsx (chunker, mic
    // scoring, karaoke, Beat-the-Bot launch) can consume the exact
    // same shape as a real user-uploaded book.
    if (typeof bookId === 'string' && bookId.startsWith('lib_')) {
      api.get(`/starter-library/${bookId}`).then(({data}) => {
        const p = data.passage;
        setBook({
          book_id: p.passage_id,
          title: p.title,
          language: p.language,
          is_library: true, // flag consumed by handoff card + delete-guards
          pages: [{
            page_id: p.passage_id + '_p0',
            text: p.text,
            ocr_version: 999, // library text is authoritative — never triggers a "rescan" banner
          }],
          current_page_index: 0,
          current_chunk_index: 0,
        });
        setPageIdx(0);
        setChunkIdx(0);
      }).catch(() => {});
      return;
    }
    api.get(`/books/${bookId}`).then(({data}) => {
      setBook(data);
      const idx = pageId ? data.pages.findIndex(p => p.page_id === pageId) : (data.current_page_index || 0);
      setPageIdx(idx >= 0 ? idx : 0);
      setChunkIdx(pageId ? 0 : (data.current_chunk_index || 0));
      const resuming = !pageId && ((data.current_page_index || 0) > 0 || (data.current_chunk_index || 0) > 0);
      if (resuming) { setShowResumeBanner(true); setTimeout(() => setShowResumeBanner(false), 5000); }
    });
  }, [bookId, pageId]);

  if (!book) return <div className="p-8 text-center"><Loader2 className="animate-spin mx-auto" /></div>;

  const page = book.pages[pageIdx];
  const chunks = chunkText(page.text || '', CHUNK_SIZES[chunkSize] || 10);
  const safeChunkIdx = Math.min(chunkIdx, Math.max(0, chunks.length - 1));
  const currentChunk = chunks[safeChunkIdx] || '';
  // Language derived from the ACTUAL text being read, NOT `book.language`.
  // Root cause of iter-22 Hindi regression: books get tagged `en` at creation
  // (child only has EN registered, OCR gets Hindi anyway, book.language stays
  // `en`). When we then send Hindi audio with `language=en`, Whisper's
  // fallback translates Hindi → English instead of transcribing — the exact
  // bug we saw ("To touch or speak with a teacher..."). Detecting Devanagari
  // on the chunk text is robust: Hindi pages have Devanagari, English pages
  // don't, and bilingual books get per-chunk classification for free.
  const sessionLang = /[\u0900-\u097F]/.test(currentChunk) ? 'hi' : (book.language || 'en');
  const t = RL(sessionLang);
  const gender = activeChild?.voice_gender || 'female';

  // Iter-51.3: composite key for the "first attempt required" gate.
  const chunkKey = page ? `${page.page_id}::${safeChunkIdx}` : '';
  const hasAttempted = attemptedChunks.has(chunkKey);
  const markAttempted = () => {
    if (!chunkKey) return;
    setAttemptedChunks(prev => {
      if (prev.has(chunkKey)) return prev;
      const next = new Set(prev);
      next.add(chunkKey);
      return next;
    });
  };

  const speak = (text, opts = {}) => {
    // Defensive language pick: if the utterance contains any Devanagari
    // character, force `hi` even if the caller passed no language. Prevents
    // an English TTS voice from awkwardly voicing Hindi text.
    const forcedLang = /[\u0900-\u097F]/.test(text) ? 'hi' : (opts.language || sessionLang);
    return tts.play(text, { language: forcedLang, speed: opts.speed ?? sessionSpeed, voice_gender: gender });
  };

  const saveChunkPref = async (v) => {
    // Iter-51: preserve the child's reading position when they change chunk
    // size mid-page. Old behavior reset to chunk 0, forcing them to press
    // Next repeatedly to catch up. Fix: find where the CURRENT chunk starts
    // inside the page text, then locate the new-size chunk that CONTAINS
    // that same character offset. Falls back to chunk 0 only for edge cases.
    const oldChunks = chunkText(page.text || '', CHUNK_SIZES[chunkSize] || 10);
    const oldChunk = oldChunks[safeChunkIdx] || '';
    const anchorNeedle = stripMarkers(oldChunk).split(/\s+/).slice(0, 4).join(' ');
    const newChunks = chunkText(page.text || '', CHUNK_SIZES[v] || 10);
    let newIdx = 0;
    if (anchorNeedle) {
      const found = newChunks.findIndex(c => stripMarkers(c).includes(anchorNeedle));
      if (found >= 0) newIdx = found;
    }
    setChunkSize(v); setChunkIdx(newIdx);
    try { await api.put(`/children/${activeChild.child_id}`, { chunk_size_preset: v }); updateActiveChild({ chunk_size_preset: v }); } catch {}
  };

  // Iter-50: persist the on-screen speed slider back to the child profile
  // so tomorrow's session picks up where they left off — a young reader
  // should never have to reset "very slow" every day.
  const saveSpeedPref = async (v) => {
    if (!activeChild) return;
    try { await api.put(`/children/${activeChild.child_id}`, { tts_speed: v }); updateActiveChild({ tts_speed: v }); } catch {}
  };

  const startMic = async () => {
    // Iter-51.7 perf: flip the recording UI IMMEDIATELY so the child sees
    // the mic button turn red on tap, THEN await getUserMedia. On a cold
    // start Chrome's permission handshake takes 100-500 ms and Safari
    // sometimes 700 ms — the old flow left the button "dead" for that
    // whole window, feeling like a lag bug.
    setResult(null); setCoachWord(null); setRecording(true);
    // Iter-51.8: if the previous scoring is still in flight when the child
    // taps mic again, ABANDON it so they don't sit staring at a scoring
    // spinner. Deepgram + /sessions can take multiple seconds on a poor
    // network, and forcing the child to wait to record again is what the
    // production report called "mic slowness".
    if (processing) {
      try { scoreAbortRef.current?.abort?.(); } catch {}
      setProcessing(false);
    }
    try {
      const stream = await getSharedStream();
      // Pick a mimeType the browser actually supports. Safari/iOS doesn't do
      // audio/webm — fall back to audio/mp4 there. Without this, MediaRecorder
      // silently emits an empty blob and Deepgram returns garbage like
      // "greeting card".
      const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus', ''];
      const mimeType = candidates.find(m => !m || (window.MediaRecorder?.isTypeSupported?.(m)));
      // Iter-51.9 prod perf fix: force a LOW audio bitrate. MediaRecorder's
      // default is 128 kbps for opus/mp4 — a 15 s recording is ~240 KB. On
      // Indian cellular upstream (often 200-500 kbps) that upload alone can
      // eat 5-15 s of the "mic is slow" spinner. 24 kbps Opus is still well
      // above what Deepgram / Whisper need for accurate STT (they down-
      // sample to 16 kHz internally), and shrinks a 15 s clip to ~45 KB —
      // a 5× upload speedup on the same network.
      const mrOpts = {
        ...(mimeType ? { mimeType } : {}),
        audioBitsPerSecond: 24000,
      };
      const mr = new MediaRecorder(stream, mrOpts);
      chunksRef.current = [];
      mr.ondataavailable = e => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        // Iter-51.8: do NOT stop the underlying stream tracks here — we
        // keep the stream alive across recordings so the next mic tap is
        // instant. The stream is only released on component unmount (see
        // the effect at mount time).
        const blobType = mr.mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: blobType });
        if (blob.size < 1024) {
          toast.error(sessionLang === 'hi' ? 'आवाज़ ठीक से रिकॉर्ड नहीं हुई — फिर से बोलो।' : "I couldn't hear you — please try again.");
          setProcessing(false);
          return;
        }
        setMyAudioUrl(URL.createObjectURL(blob));
        await scoreReading(blob);
      };
      mediaRef.current = mr;
      // Timeslice ensures ondataavailable fires every 500ms — critical so a
      // fast stop still gets audio out of MediaRecorder.
      mr.start(500);
    } catch {
      // Roll back the optimistic recording flip if permission was denied.
      setRecording(false);
      toast.error(sessionLang === 'hi' ? 'माइक की अनुमति चाहिए' : 'Mic permission needed');
    }
  };
  const stopMic = () => {
    const mr = mediaRef.current;
    if (mr && mr.state !== 'inactive') {
      // Force any buffered audio out before stop() closes the encoder.
      try { mr.requestData(); } catch {}
      mr.stop();
    }
    setRecording(false);
  };

  const detectGoodbye = (text) => {
    const s = (text || '').toLowerCase();
    const keywords = ['bye', 'goodbye', 'phir milenge', 'फिर मिलेंगे', 'कल मिलते हैं', 'जा रहा हूँ', 'जा रही हूँ',
      'अब जाना है', 'खाना खाना है', 'बंद करो', 'रुको', 'बस अब', 'खत्म'];
    return keywords.some(k => s.includes(k.toLowerCase()));
  };

  const scoreReading = async (blob) => {
    setProcessing(true);
    // Iter-51.3: any STT run — even an empty transcript — counts as a
    // first attempt so the Read-to-me gate lifts. This is intentional:
    // if the child taps mic and stays silent, they've still engaged with
    // the "try reading first" step and it would be cruel to keep the
    // hint button locked.
    markAttempted();
    // Iter-51.8: attach an AbortController so a fresh mic tap can cancel
    // an in-flight scoring call. Without this, the child had to wait for
    // Deepgram + /sessions to finish before re-recording, which on a bad
    // network looked like "the mic button is spinning forever".
    const controller = new AbortController();
    scoreAbortRef.current = controller;
    // Iter-51.9 prod telemetry: measure every phase so we can see WHERE
    // the "mic is slow" seconds are actually being spent on production —
    // upload, STT, or /sessions. Numbers land in window.__micTimings and
    // console (prefix [mic-perf]) so a parent can copy hard numbers from
    // readbuddy.co.in DevTools instead of "it feels slow".
    const t0 = performance.now();
    const blobKb = Math.round((blob?.size || 0) / 1024);
    // eslint-disable-next-line no-console
    console.log('[mic-perf] scoreReading start', { blob_kb: blobKb, lang: sessionLang });
    try {
      setScorePhase('uploading');
      const form = new FormData();
      form.append('audio', blob, 'read.webm');
      form.append('language', sessionLang);
      const tSttStart = performance.now();
      // Iter-51.9 axios timeouts: default axios has no timeout — a hung
      // Deepgram/network stalls the spinner FOREVER. 45 s is far above
      // p99 Deepgram + upload from Indian cellular (~10 s) so real users
      // never trip it; but if network dies we surface a real error
      // instead of an infinite spinner.
      const stt = await api.post('/stt', form, {
        signal: controller.signal,
        timeout: 45000,
        onUploadProgress: (evt) => {
          // Once upload is fully sent, we're in "waiting on Deepgram" land.
          if (evt?.loaded && evt?.total && evt.loaded >= evt.total) setScorePhase('scoring');
        },
      });
      const tSttEnd = performance.now();
      const sttMs = Math.round(tSttEnd - tSttStart);
      setScorePhase('scoring');
      const spokenText = stt.data.text || '';
      const spokenWords = stt.data.words || null;
      setLastTranscript(spokenText);
      // Iter-51.9: pull server-side timing so the console prints both
      // client-observed and server-observed numbers. If client_ms - server_ms
      // is 5-20 s the bottleneck is the UPLOAD (blob too big / slow upstream);
      // if server_ms itself is 20+ s the bottleneck is DEEPGRAM.
      const srv = stt.data?.timing_ms || {};
      // eslint-disable-next-line no-console
      console.log('[mic-perf] stt done', {
        client_ms: sttMs, server_total_ms: srv.total, server_dg_ms: srv.deepgram,
        server_whisper_ms: srv.whisper, server_bytes: srv.bytes, upload_est_ms: srv.total ? Math.max(0, sttMs - srv.total) : null,
        provider: stt.data.provider, chars: spokenText.length,
      });
      // Empty transcript (silence / hallucination filtered server-side) —
      // don't spam the child with a "0% accuracy" screen. Just prompt them
      // to try again.
      if (!spokenText.trim()) {
        toast(sessionLang === 'hi' ? 'मुझे कुछ सुनाई नहीं दिया — फिर से बोलो।' : "I didn't hear you — please read again.");
        setProcessing(false);
        setScorePhase('');
        return;
      }
      // Check for goodbye phrase
      if (detectGoodbye(spokenText)) {
        setProcessing(false);
        setScorePhase('');
        return sayGoodbye();
      }
      setScorePhase('saving');
      const tSessStart = performance.now();
      const { data } = await api.post('/sessions', {
        child_id: activeChild.child_id, book_id: book.book_id, page_id: page.page_id,
        chunk_index: safeChunkIdx, language: sessionLang,
        // Iter-51: send the marker-free chunk so backend tokenization and
        // marked_words align cleanly with what the child actually READ. The
        // display layer re-adds the visual highlighting from currentChunk.
        original_text: stripForSpeech(currentChunk), spoken_text: spokenText,
        spoken_words: spokenWords,
      }, { signal: controller.signal, timeout: 20000 });
      const sessMs = Math.round(performance.now() - tSessStart);
      const totalMs = Math.round(performance.now() - t0);
      const timings = { blob_kb: blobKb, stt_ms: sttMs, sess_ms: sessMs, total_ms: totalMs, provider: stt.data.provider };
      setLastTimings(timings);
      try { window.__micTimings = timings; } catch {}
      // eslint-disable-next-line no-console
      console.log('[mic-perf] all done', timings);
      if (data && data.truncated) {
        const msg = sessionLang === 'hi' ? data.message_hi : data.message_en;
        toast(msg);
        speak(msg);
        setResult(null);
        return;
      }
      setResult(data);
      // Iter-52.30 — DOUBLE-VOICE FIX on the review/scoring screen.
      // Previously we ALWAYS spoke `data.feedback` here, AND — 900ms
      // later — fired `celebrateMilestone(...)` when milestones were
      // present. But the feedback line typically runs 1.5–3 seconds
      // and `celebrateMilestone` plays via a SEPARATE `new Audio()`
      // element (see /app/frontend/src/lib/milestones.js) that
      // `tts.stop()` can't reach. Result: parent heard the feedback
      // voice AND the celebration voice at the same time, perceived
      // as "Anjali AND Gaurav" double-voice, especially at the end
      // of a Starter Library English passage where a `perfect` or
      // `streak` milestone almost always fires.
      // Fix: when a milestone will play, skip the feedback speak —
      // the celebration IS the audio feedback. The written feedback
      // text still renders on-screen, and iter-52.16's `silent` flag
      // still guards milestone-vs-milestone stacking.
      const hasMilestone = Array.isArray(data.milestones) && data.milestones.length > 0;
      if (hasMilestone) {
        try { tts.stop(); } catch { /* noop */ }
      } else {
        speak(data.feedback);
      }
      // Milestone bursts (streak / reward-hit / 100%). Fire confetti +
      // cached voice line WITHOUT awaiting so the reading flow proceeds.
      // We defer the audio a beat so the feedback line ("Great job…")
      // isn't drowned out by the milestone phrase.
      // Iter-52.16 — DOUBLE-VOICE FIX. Previously, when the backend
      // returned N milestones we fired N `celebrateMilestone` calls
      // in a tight loop. Each one downloads/plays its OWN audio blob
      // via `new Audio()` (see `lib/milestones.js`), so two milestones
      // for the same event (e.g. perfect + streak on the last chunk
      // of a Starter Library English passage) overlapped and the
      // parent heard two voices at once. We now:
      //   1. Confetti every milestone (visual celebration is cheap
      //      and delightful), but
      //   2. Speak the voice line for only the FIRST milestone.
      // This preserves the burst without stacking narration.
      if (Array.isArray(data.milestones) && data.milestones.length > 0) {
        setTimeout(() => {
          const first = data.milestones[0];
          celebrateMilestone({
            kind: first.type,
            language: sessionLang,
            voiceGender: activeChild.voice_gender || 'female',
          });
          for (let i = 1; i < data.milestones.length; i++) {
            celebrateMilestone({
              kind: data.milestones[i].type,
              language: sessionLang,
              voiceGender: activeChild.voice_gender || 'female',
              silent: true,
            });
          }
        }, 900);
      }
    } catch (err) {
      // A user-triggered abort (fresh mic tap) is expected — don't toast.
      if (err?.name === 'CanceledError' || err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return;
      // Timeout → tell the parent what's actually wrong, not a generic
      // "could not score". Different copy so they know to check network.
      const totalMs = Math.round(performance.now() - t0);
      // eslint-disable-next-line no-console
      console.warn('[mic-perf] scoring failed', { after_ms: totalMs, code: err?.code, name: err?.name, message: err?.message });
      if (err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || '')) {
        toast.error(sessionLang === 'hi' ? 'नेटवर्क धीमा है — फिर से कोशिश करो।' : 'Network is slow — please try again.');
      } else {
        toast.error(sessionLang === 'hi' ? 'स्कोरिंग विफल' : 'Could not score reading');
      }
    }
    finally {
      if (scoreAbortRef.current === controller) scoreAbortRef.current = null;
      setProcessing(false);
      setScorePhase('');
    }
  };

  const goNext = () => {
    setResult(null); setMyAudioUrl(null); setCoachWord(null); setMeaningText(null); setSummaryText(null);
    if (safeChunkIdx + 1 < chunks.length) setChunkIdx(safeChunkIdx + 1);
    else if (pageIdx + 1 < book.pages.length) { setPageIdx(pageIdx + 1); setChunkIdx(0); }
    // Iter-52.15 — at the end of a Starter Library passage, show the
    // handoff card ("Great reading! Now try a page from your own book")
    // BEFORE bouncing back to Dashboard. Library passages are designed
    // as a bridge into the scanner, not an end-state.
    else if (book?.is_library) setShowLibraryHandoff(true);
    else nav('/dashboard');
  };
  const goPrev = () => {
    setResult(null); setMyAudioUrl(null); setCoachWord(null); setMeaningText(null); setSummaryText(null);
    if (safeChunkIdx > 0) setChunkIdx(safeChunkIdx - 1);
    else if (pageIdx > 0) {
      const prev = pageIdx - 1;
      const prevChunks = chunkText(book.pages[prev].text || '', CHUNK_SIZES[chunkSize] || 10);
      setPageIdx(prev); setChunkIdx(Math.max(0, prevChunks.length - 1));
    }
  };
  // Whole-page jumps — added alongside chunk-nav so parents can skim
  // through a long book without tapping chunk-next 10× per page. Always
  // land on chunk 0 of the destination page so the child starts from the
  // top. Buttons at boundaries disable rather than wrap around (feels
  // safer for a young reader).
  const goNextPage = () => {
    if (pageIdx + 1 >= book.pages.length) return;
    setResult(null); setMyAudioUrl(null); setCoachWord(null); setMeaningText(null); setSummaryText(null);
    setPageIdx(pageIdx + 1); setChunkIdx(0);
  };
  const goPrevPage = () => {
    if (pageIdx === 0) return;
    setResult(null); setMyAudioUrl(null); setCoachWord(null); setMeaningText(null); setSummaryText(null);
    setPageIdx(pageIdx - 1); setChunkIdx(0);
  };

  const startCoach = (word) => {
    setCoachWord(word); setCoachStage('prompt');
    // What the app SAYS out loud must match the session's language so a
    // child in a Hindi lesson isn't suddenly given an English instruction.
    // (The panel LABEL stays in English — parent-facing UI is English-only.)
    const spokenPrompt = sessionLang === 'hi'
      ? `एक बार फिर बोलो — ${word} — ${word}`
      : `Say '${word}' one more time — ${word}`;
    // Coach speaks slower than the reading pace — the child needs a beat to
    // mentally re-attempt the word.
    tts.play(spokenPrompt, { language: sessionLang, speed: 0.65, voice_gender: gender });
  };
  const coachMic = async () => {
    try {
      // Iter-51.8: reuse the shared audio stream — otherwise every coach
      // attempt would re-run getUserMedia (100-500 ms). Same reason as the
      // main mic path.
      const stream = await getSharedStream();
      const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus', ''];
      const mimeType = candidates.find(m => !m || (window.MediaRecorder?.isTypeSupported?.(m)));
      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      const arr = [];
      mr.ondataavailable = e => { if (e.data && e.data.size) arr.push(e.data); };
      mr.onstop = async () => {
        // Iter-51.8: keep the stream alive across recordings; only unmount
        // releases it.
        const blob = new Blob(arr, { type: mr.mimeType || 'audio/webm' });
        if (blob.size < 512) { setCoachStage('prompt'); return; }
        const form = new FormData();
        form.append('audio', blob, 'coach.webm'); form.append('language', sessionLang);
        try {
          const stt = await api.post('/stt', form);
          const spoken = (stt.data.text || '').toLowerCase().replace(/[.,!?।"]+/g, '').trim();
          const target = coachWord.toLowerCase().replace(/[.,!?।"]+/g, '').trim();
          const ok = spoken.includes(target) || target.includes(spoken);
          if (ok) {
            setCoachStage('done');
            const doneSpoken = sessionLang === 'hi' ? 'शाबाश! बहुत बढ़िया!' : "Well done! Nice!";
            tts.play(doneSpoken, { language: sessionLang, speed: 0.75, voice_gender: gender });
          } else {
            setCoachStage('prompt');
            tts.play(coachWord + '. ' + coachWord, { language: sessionLang, speed: 0.65, voice_gender: gender });
          }
        } catch { setCoachStage('prompt'); }
      };
      mediaRef.current = mr; mr.start(500); setCoachStage('listening');
      setTimeout(() => { const m = mediaRef.current; if (m && m.state !== 'inactive') { try { m.requestData(); } catch {} m.stop(); } }, 3500);
    } catch { toast.error(sessionLang === 'hi' ? 'माइक की अनुमति चाहिए' : 'Mic permission needed'); }
  };

  const loadMeaning = async () => {
    setMeaningLoading(true);
    try {
      const { data } = await api.post('/lessons/meaning', { text: stripMarkers(currentChunk), language: sessionLang });
      setMeaningText(data.meaning);
      tts.play(data.meaning, { language: data.target_language || 'hi', speed: sessionSpeed, voice_gender: gender });
    } catch { toast.error(sessionLang === 'hi' ? 'अर्थ नहीं मिला' : 'Could not fetch meaning'); }
    finally { setMeaningLoading(false); }
  };
  const loadSummary = async (forceLang) => {
    // Iter-51.2: parents can force English OR Hindi regardless of the
    // source chunk's language — passed via `forceLang` from the two
    // dedicated summary buttons. Falls back to the source-language
    // heuristic if a caller omits it (kept for any legacy callers).
    const summaryLang = forceLang || sessionLang;
    setSummaryLoading(true);
    try {
      const { data } = await api.post('/lessons/summary', {
        text: stripMarkers(currentChunk),
        language: summaryLang,
        source_language: sessionLang,
      });
      setSummaryText(data.summary);
      tts.play(data.summary, { language: summaryLang, speed: sessionSpeed, voice_gender: gender });
    } catch { toast.error(summaryLang === 'hi' ? 'सार नहीं बना' : 'Could not summarize'); }
    finally { setSummaryLoading(false); }
  };
  const sayGoodbye = async () => {
    if (goodbyeState !== 'idle') return;
    setGoodbyeState('preparing');
    try {
      const { data } = await api.post('/lessons/goodbye', {
        child_id: activeChild.child_id,
        session_language: sessionLang,
      });
      const lang = data.language || sessionLang;
      const goodbyeSpeed = 0.65;
      setGoodbyeText(data.prompt_text || data.text || '');
      setGoodbyeState('greeting');
      await speak(data.prompt_text || data.text, { language: lang, speed: goodbyeSpeed });
      // 2. Open mic ~5 s for the child to say "bye" (or Hindi equivalent).
      setGoodbyeState('listening');
      let heardGoodbye = false;
      try {
        const stream = await getSharedStream();
        const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', ''];
        const mimeType = candidates.find(m => !m || (window.MediaRecorder?.isTypeSupported?.(m)));
        const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
        const arr = [];
        mr.ondataavailable = e => { if (e.data && e.data.size) arr.push(e.data); };
        await new Promise((resolve) => {
          mr.onstop = async () => {
            // Iter-51.8: keep the shared stream alive; only unmount releases it.
            const blob = new Blob(arr, { type: mr.mimeType || 'audio/webm' });
            if (blob.size >= 512) {
              try {
                const form = new FormData();
                form.append('audio', blob, 'goodbye.webm');
                form.append('language', lang);
                const stt = await api.post('/stt', form);
                if (detectGoodbye(stt.data.text || '')) heardGoodbye = true;
              } catch {}
            }
            resolve();
          };
          mr.start(500);
          setTimeout(() => { try { mr.requestData(); } catch {} if (mr.state !== 'inactive') mr.stop(); }, 5000);
        });
      } catch { /* mic denied — fall through to a warm farewell anyway */ }
      const finalText = heardGoodbye ? data.ack_text : (data.no_bye_text || data.ack_text);
      setGoodbyeText(finalText || '');
      setGoodbyeState('closing');
      await speak(finalText, { language: lang, speed: goodbyeSpeed });
      nav('/dashboard');
    } catch { nav('/dashboard'); }
    finally { setGoodbyeState('idle'); }
  };

  // Iter-51: marker-aware token stream for rendering. Splits currentChunk
  // into a mixed list of {type, ...} nodes so the JSX can:
  //   - render `## Heading:` lines with bold + underline styling,
  //   - render `==word word==` (multi-word) spans with a yellow highlighter
  //     background across every word inside — highlight tokenization runs
  //     BEFORE whitespace-splitting so `==water cycle==` doesn't degrade
  //     into two half-marker tokens `==water` + `cycle==`,
  //   - preserve per-word tap-to-syllable + missed-word coaching,
  //   - overlay marked_words statuses in the same order they arrive.
  const renderTokens = (() => {
    const marked = result?.marked_words || null;
    let markedIdx = 0;
    const nextStatus = () => {
      if (!marked) return 'default';
      while (markedIdx < marked.length && marked[markedIdx].status === 'space') markedIdx++;
      const st = markedIdx < marked.length ? marked[markedIdx].status : 'default';
      markedIdx++;
      return st;
    };
    // Helper: given a line's text (after the optional `## ` prefix stripped)
    // return an ordered list of {text, highlighted} spans by cutting the
    // line at every `==…==` boundary FIRST — no whitespace-splitting yet.
    const spansOf = (line) => {
      const out = [];
      let last = 0;
      const re = /==([^=\n]+)==/g;
      let m;
      while ((m = re.exec(line)) !== null) {
        if (m.index > last) out.push({ text: line.slice(last, m.index), highlighted: false });
        out.push({ text: m[1], highlighted: true });
        last = m.index + m[0].length;
      }
      if (last < line.length) out.push({ text: line.slice(last), highlighted: false });
      return out;
    };
    const out = [];
    const lines = (currentChunk || '').split('\n');
    lines.forEach((line, li) => {
      const isHeading = line.startsWith(HEADING_PREFIX);
      const body = isHeading ? line.slice(HEADING_PREFIX.length) : line;
      const spans = spansOf(body);
      // Iter-52.55.4 — after the first "real word" of a line is seen,
      // stop treating leading tokens as prefix markers. Prevents a
      // mid-sentence "1." (rare) from being silently swallowed.
      let seenRealWord = false;
      spans.forEach((span) => {
        const parts = span.text.split(/(\s+)/);
        for (const p of parts) {
          if (!p) continue;
          if (/^\s+$/.test(p)) {
            out.push({ type: 'space', text: p, isHeading, highlighted: span.highlighted });
          } else if (!seenRealWord && isNonSpeechPrefixToken(p)) {
            // Iter-52.55.4 — prefix bullet/label token. Display it but
            // do NOT increment wordIdx (karaoke) and do NOT consume a
            // marked_words status slot (mic scoring). This is what
            // lets `• The sky is blue.` show the bullet on screen
            // while TTS + STT only handle "The sky is blue.".
            out.push({
              type: 'prefix_marker',
              display: p,
              highlighted: span.highlighted,
              isHeading,
            });
          } else {
            seenRealWord = true;
            out.push({
              type: 'word',
              display: p,
              highlighted: span.highlighted,
              isHeading,
              status: nextStatus(),
            });
          }
        }
      });
      if (li < lines.length - 1) out.push({ type: 'newline' });
    });
    return out;
  })();
  const missedWords = result ? Array.from(new Set((result.missed_words || []).map(w => w.replace(/[.,!?।"]+$/g, '')).filter(Boolean))) : [];
  const hiFont = sessionLang === 'hi' ? 'font-hi' : '';

  return (
    <div className="px-4 md:px-6 pt-4 pb-44 max-w-6xl mx-auto overflow-x-hidden">
      {/* Iter-52.1 — AI-disclaimer is now PERMANENT and placed at the
          bottom of the viewport (rendered near the end of this JSX
          tree so its DOM order matches its visual placement). */}
      <div className="flex justify-between items-center mb-3 gap-2 flex-wrap">
        <button onClick={() => nav(`/book/${book.book_id}`)} className="text-slate-500 font-bold text-sm inline-flex items-center gap-1"><ChevronLeft size={16}/> Book</button>
        <div className="flex items-center gap-2 text-xs md:text-sm font-bold text-slate-500">
          <span>P {pageIdx + 1}/{book.pages.length}</span><span>·</span>
          {/* The "C N/N" chunk indicator doubles as the invisible parent
              tap-target. Piggybacking on an already-rendered visible span
              is far more reliable than the previous approaches (opacity-0
              button, transparent-bg button) which repeatedly failed mobile
              hit-testing. Kids won't tap the chunk counter 10× rapidly by
              accident. We attach BOTH onPointerDown (fires immediately on
              touch, no 300 ms delay) and onClick (mouse fallback). */}
          <span
            data-testid="debug-secret-tap"
            role="presentation"
            onPointerDown={registerSecretTap}
            onClick={registerSecretTap}
            className="select-none cursor-default"
          >
            C {safeChunkIdx + 1}/{chunks.length || 1}
          </span>
          {tapProgress >= 5 && (
            <span data-testid="debug-tap-progress" aria-hidden="true"
              className="w-1.5 h-1.5 rounded-full bg-slate-400/60"
            />
          )}
        </div>
      </div>

      {showResumeBanner && (
        <div data-testid="resume-banner" className="bg-[#F4B740] rounded-2xl p-3 mb-4 flex items-center gap-3">
          <div className="text-2xl">📖</div>
          <div className="flex-1 min-w-0">
            <div className={`font-extrabold text-[#2B2140] ${hiFont}`}>{t('welcomeBack')}</div>
            <div className={`text-sm text-[#2B2140]/70 ${hiFont}`}>{t('lastReadHere')} — P {pageIdx + 1}, C {safeChunkIdx + 1}</div>
          </div>
        </div>
      )}

      {/* Split-screen */}
      <div data-testid="split-screen" className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div className="bg-white rounded-3xl p-3">
          {page.image_base64 ? (
            <img data-testid="page-image-view" src={page.image_base64} alt="page" className="w-full h-[280px] md:h-[420px] object-contain rounded-2xl" style={{ filter: 'contrast(1.08) brightness(1.04) saturate(1.05)' }}/>
          ) : (
            <div className="w-full h-[280px] md:h-[420px] rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400"><BookOpen size={40}/></div>
          )}
        </div>
        <div className="bg-white rounded-3xl p-5 md:p-8 flex flex-col min-w-0">
          {(() => {
            // Iter-52.10 — a match_exercise chunk gets its OWN dedicated
            // visual: instruction on top, two labeled columns side-by-side.
            // Word-tap-to-syllables + karaoke-highlight are intentionally
            // NOT applied here — a matching exercise reads item-by-item
            // rather than word-by-word, and the column labels ("1.", "A.")
            // aren't real reading tokens.
            const match = parseMatchExercise(currentChunk);
            if (match.isMatch) {
              return (
                <div data-testid="match-exercise" className={`flex-1 flex flex-col ${hiFont}`}>
                  <div data-testid="match-instruction"
                    className="text-lg md:text-xl font-extrabold text-[#2B2140] underline underline-offset-4 decoration-2 mb-4 text-center">
                    {match.instruction}
                  </div>
                  {/* Iter-52.14 — proper row-aligned two-column grid.
                      Previous impl used two independent flex columns which
                      drifted apart when Column A items (photos) were much
                      taller than Column B items (short text labels) — the
                      child couldn't tell which right-side option sat next
                      to which left-side picture. Rewriting as a real
                      2-col CSS grid with one grid ROW per index guarantees
                      picture 1 ↔ option A, picture 2 ↔ option B, etc.
                      align exactly horizontally regardless of cell
                      contents. `items-center` centers each cell's
                      content vertically inside its row. */}
                  <div className="grid grid-cols-2 gap-x-3 md:gap-x-4 gap-y-3 md:gap-y-4 flex-1"
                    data-testid="match-grid">
                    <div className="text-xs md:text-sm font-extrabold uppercase tracking-wide text-[#B85582] px-1"
                      data-testid="match-left-col-header">Column A</div>
                    <div className="text-xs md:text-sm font-extrabold uppercase tracking-wide text-[#3577B8] px-1"
                      data-testid="match-right-col-header">Column B</div>
                    {Array.from({ length: Math.max(match.left.length, match.right.length) }).map((_, i) => {
                      const leftItem = match.left[i];
                      const rightItem = match.right[i];
                      const src = match.leftImages?.[i] || '';
                      const hasCrop = Boolean(src);
                      return (
                        <Fragment key={i}>
                          {/* Iter-52.11 — a cropped-picture URL means the
                              child sees the actual photograph/illustration
                              (essential for GK "photo → freedom fighter"
                              exercises where text descriptors would give
                              away the answer or be uselessly vague). Text
                              descriptor stays as accessibility alt text so
                              screen readers still convey the item, and as
                              a visible fallback if the crop 404s. */}
                          <div data-testid={`match-left-${i}`}
                            className="bg-[#FFF7EA] rounded-xl p-3 md:p-4 flex items-center gap-3 min-h-[6rem] md:min-h-[7rem] text-base md:text-xl font-bold leading-snug text-[#2B2140] break-words">
                            {leftItem != null ? (
                              <>
                                <span className="text-[#FF6B1A] shrink-0">{matchLeftLabel(i)}</span>
                                {hasCrop ? (
                                  <img
                                    data-testid={`match-left-img-${i}`}
                                    src={src}
                                    alt={leftItem}
                                    className="max-h-20 md:max-h-24 max-w-full rounded-lg bg-white shadow-sm object-contain"
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                      const fb = e.currentTarget.nextElementSibling;
                                      if (fb) fb.style.display = 'inline';
                                    }}
                                  />
                                ) : null}
                                <span
                                  data-testid={`match-left-text-${i}`}
                                  style={hasCrop ? { display: 'none' } : {}}
                                >
                                  {leftItem}
                                </span>
                              </>
                            ) : null}
                          </div>
                          <div data-testid={`match-right-${i}`}
                            className="bg-[#EAF6FF] rounded-xl p-3 md:p-4 flex items-center gap-2 min-h-[6rem] md:min-h-[7rem] text-base md:text-xl font-bold leading-snug text-[#2B2140] break-words">
                            {rightItem != null ? (
                              <>
                                <span className="text-[#3577B8] shrink-0">{matchRightLabel(i)}</span>
                                <span>{rightItem}</span>
                              </>
                            ) : null}
                          </div>
                        </Fragment>
                      );
                    })}
                  </div>
                </div>
              );
            }
            return (
          <p data-testid="reading-text" className={`text-xl md:text-2xl font-bold leading-relaxed whitespace-pre-wrap flex-1 break-words ${hiFont}`}
            style={(() => {
              // Iter-52.51 — justify body-paragraph text (MS-Word style)
              // for a cleaner right edge; keep poem/verse chunks
              // LEFT-aligned so their intentional line-breaks and
              // stanza rhythm are preserved. Heuristic: any chunk
              // with 2+ hard newlines whose average per-line width
              // is short (< 45 chars) reads as a poem.
              const raw = stripMarkers(currentChunk || '');
              const lines = raw.split('\n').filter(Boolean);
              const nlCount = (raw.match(/\n/g) || []).length;
              const avgLen = lines.length ? lines.reduce((s, l) => s + l.length, 0) / lines.length : 0;
              const isVerse = nlCount >= 2 && avgLen < 45;
              return { textAlign: isVerse ? 'left' : 'justify', hyphens: 'auto' };
            })()}
          >
            {(() => {
              // Iter-51.99 — walk tokens once, tracking a running
              // "word-token index" so the active-word idx from the
              // /tts/with-timestamps response maps 1:1 to what the
              // frontend renders (spaces & newlines don't count).
              let wordCounter = -1;
              return renderTokens.map((tk, i) => {
              if (tk.type === 'newline') {
                // Iter-51 spacing: a newline that separates a heading line
                // from the body (or vice-versa) gets extra vertical breath
                // so the heading isn't cramped against the paragraph.
                const prev = renderTokens[i - 1];
                const next = renderTokens[i + 1];
                const isHeadingBoundary = (prev && prev.isHeading) || (next && next.isHeading);
                return isHeadingBoundary
                  ? <span key={i} className="block h-3" aria-hidden />
                  : <br key={i}/>;
              }
              if (tk.type === 'space') {
                // Highlighted space keeps the yellow background continuous
                // between the words inside a multi-word `==...==` span.
                // Heading spaces also carry the underline so `The Water
                // Cycle:` reads as ONE continuous rule instead of three.
                const cls = [
                  tk.highlighted ? 'bg-[#FEF08A]' : '',
                  tk.isHeading ? 'underline underline-offset-4 decoration-2' : '',
                ].filter(Boolean).join(' ');
                return <span key={i} className={cls}>{tk.text}</span>;
              }
              if (tk.type === 'prefix_marker') {
                // Iter-52.55.4 — bullet/label prefix. Rendered in a muted
                // style so the child sees the numbering but visually knows
                // it's decorative, not something to read aloud.
                return (
                  <span key={i} className="text-[#8B7BB8] font-black mr-1 select-none">
                    {tk.display}
                  </span>
                );
              }
              // word token
              wordCounter += 1;
              const myWordIdx = wordCounter;
              // Iter-51.99 — Read-to-Me karaoke highlight for the currently
              // spoken word. Soft amber glow + gentle bg tint. Kept in a
              // dedicated ring/shadow class so it doesn't clash with the
              // existing highlighter (`bg-[#FEF08A]`) or the missed/correct
              // status colors.
              const karaokeCls = (activeWordIdx === myWordIdx)
                ? 'bg-[#FFF3E0] ring-2 ring-[#FF8A3D]/70 rounded-md px-0.5 shadow-[0_0_14px_rgba(255,138,61,0.55)] transition-all duration-100'
                : '';
              const statusCls = tk.status === 'correct' ? 'word-correct'
                              : tk.status === 'missed'  ? 'word-missed cursor-pointer'
                              : 'word-default cursor-pointer';
              const headingCls = tk.isHeading ? 'underline underline-offset-4 decoration-2 text-[#2B2140]' : '';
              // Iter-51: yellow-highlighter background to preserve marker
              // emphasis from the source book. Applied to the whole span
              // so the highlight naturally follows the styled word. For
              // multi-word highlights the neighbouring space token above
              // also gets the yellow bg so the highlighter reads as one
              // continuous strip.
              const highlightCls = tk.highlighted
                ? 'bg-[#FEF08A] ring-1 ring-[#FACC15]/60'
                : '';
              const cleanForTap = tk.display.replace(/^[\s'"“”‘’(\[{]+|[\s'"“”‘’)\]}.,;:!?।॥\-—–]+$/g, '');
              // Iter-51.88 — precompute the 0-based occurrence of THIS
              // tapped word inside the sentence so the backend word-
              // slicer picks the RIGHT segment when a word repeats
              // (e.g. "run run run"). We count prior word-tokens with
              // the same normalized display before this index.
              const wordOccurrence = (() => {
                const target = cleanForTap.toLowerCase();
                let n = 0;
                for (let j = 0; j < i; j++) {
                  const other = renderTokens[j];
                  if (!other || other.type !== 'word') continue;
                  const otherClean = other.display.replace(/^[\s'"“”‘’(\[{]+|[\s'"“”‘’)\]}.,;:!?।॥\-—–]+$/g, '').toLowerCase();
                  if (otherClean === target) n++;
                }
                return n;
              })();
              const onWordTap = (e) => {
                if (!cleanForTap) return;
                e.stopPropagation();
                // Iter-51: per-word script detection so an English loanword
                // inside a Hindi chunk (or vice-versa) opens the syllable
                // popover in the right language.
                const wordLang = /[\u0900-\u097F]/.test(cleanForTap) ? 'hi' : 'en';
                setSyllableWord({
                  word: cleanForTap,
                  language: wordLang,
                  sentence: stripMarkers(currentChunk),
                  wordIndex: wordOccurrence,
                });
              };
              const commonProps = {
                className: `${statusCls} ${headingCls} ${highlightCls} ${karaokeCls}`.trim(),
                onClick: onWordTap,
                'data-word-idx': myWordIdx,
              };
              if (tk.status === 'missed') {
                return (
                  <span data-testid={`missed-word-${i}`} key={i} {...commonProps}
                    onDoubleClick={() => startCoach(cleanForTap)}>
                    {tk.display}
                  </span>
                );
              }
              return (
                <span data-testid={`chunk-word-${i}`} key={i} {...commonProps}>
                  {tk.display}
                </span>
              );
            });
            })()}
          </p>
            );
          })()}
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          GROUP 1 · READ — the primary reading loop.
          Big mic (biggest button on the page), then a compact card that
          groups the Reading Speed slider with the "Read to me" pill so
          both listening controls sit together. Order matters: READ →
          UNDERSTAND → CHALLENGE → NAVIGATE → DONE. Iter-51.30 grouping.
      ────────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-center gap-3 flex-wrap mb-3">
        {!recording ? (
          <button data-testid="mic-start-btn" onClick={startMic}
            className="bg-[#FF6B1A] text-white font-bold px-7 py-4 rounded-full inline-flex items-center gap-2 text-lg relative">
            <Mic size={26} strokeWidth={3}/>
            {t('yourTurn')}
          </button>
        ) : (
          <button data-testid="mic-stop-btn" onClick={stopMic}
            className="bg-[#E8435C] text-white font-bold px-7 py-4 rounded-full inline-flex items-center gap-2 text-lg relative recording-pulse">
            <Square size={22} strokeWidth={3} className="fill-white"/> {t('stopMic')}
          </button>
        )}
        {processing && !recording && (
          <span data-testid="scoring-indicator" className="inline-flex items-center gap-1 text-sm font-bold text-[#B85582]">
            <Loader2 className="animate-spin" size={16}/>
            {scorePhase === 'uploading' ? (sessionLang === 'hi' ? 'आवाज़ भेज रहे हैं…' : 'Uploading voice…')
             : scorePhase === 'saving'  ? (sessionLang === 'hi' ? 'स्कोर सहेज रहे हैं…' : 'Saving score…')
             :                            (sessionLang === 'hi' ? 'सुन रहे हैं…'      : 'Checking pronunciation…')}
          </span>
        )}
        {!processing && !recording && lastTimings && (
          <span data-testid="mic-timings-hint"
            title={`Blob ${lastTimings.blob_kb} KB · STT ${lastTimings.stt_ms} ms · Save ${lastTimings.sess_ms} ms · Total ${lastTimings.total_ms} ms`}
            className="text-[10px] font-bold text-slate-400 tabular-nums">
            {lastTimings.total_ms} ms
          </span>
        )}
      </div>

      {syllableWord && (
        <SyllablePopover
          word={syllableWord.word || syllableWord}
          language={syllableWord.language || sessionLang}
          sentence={syllableWord.sentence || ''}
          wordIndex={syllableWord.wordIndex || 0}
          ttsPlay={tts.play}
          voiceGender={gender}
          onClose={() => setSyllableWord(null)}
        />
      )}

      {/* GROUP 1 (cont.) — Listening controls: speed slider + Read-to-me.
          Both share one white card so they read as a single "how I
          listen" cluster. Read-to-me is a full-width pill so it lines
          up flush with the slider on mobile. */}
      <div data-testid="listen-group" className="bg-white rounded-2xl border border-slate-200 p-3 mb-3 max-w-md mx-auto">
        <label className="flex items-center gap-2 mb-2">
          <span className={`text-[10px] font-bold uppercase text-[#2B2140]/70 shrink-0 ${hiFont}`}>{t('readingSpeed')}</span>
          <input data-testid="onscreen-speed-slider" type="range"
            min={0.3} max={1.2} step={0.05} value={sessionSpeed}
            onChange={(e) => setSessionSpeed(Number(e.target.value))}
            onMouseUp={(e) => saveSpeedPref(Number(e.target.value))}
            onTouchEnd={(e) => saveSpeedPref(Number(e.target.value))}
            className="flex-1 accent-[#FF6B1A] min-w-0"/>
          <span data-testid="onscreen-speed-value" className="text-[11px] font-bold text-[#FF6B1A] w-10 text-right">{sessionSpeed.toFixed(2)}x</span>
        </label>
        {/* Iter-51.3: Read-to-me is gated behind the child's first attempt
            on this chunk. Grayed-out + "Try reading first" label until the
            STT flow has run once — so the child can't hear the correct
            pronunciation and simply mimic it back for a fake score.

            Iter-51.99 — karaoke word-highlight. When Read-to-Me runs, the
            active word gets an amber glow + soft bg tint (see the word
            renderer above). Sync is driven by the new
            `/api/tts/with-timestamps` endpoint returning word_spans, and
            stays automatic at ANY speed (0.3-1.2x) because HTMLAudio's
            currentTime is source-time. Gating rule UNTOUCHED — the
            button still requires `hasAttempted`. */}
        <button data-testid="read-to-me-btn" onClick={() => {
          setActiveWordIdx(-1);
          // Iter-52.16b — CRITICAL fix. `currentChunk` may contain the
          // `[[MATCH_EXERCISE]]{json}[[/MATCH_EXERCISE]]` sentinel, which
          // if sent raw to ElevenLabs makes the voice speak the JSON
          // (attribute names, brackets, image URLs) for minutes without
          // stopping. Every other consumer (STT / meaning / questions)
          // already routes through `stripMarkers`; Read-to-Me was the
          // one hold-out. `stripMarkers` expands the sentinel into
          // "Match the following. Column A: … Column B: …" which is
          // exactly what the child sees on screen.
          const cleanText = stripForSpeech(currentChunk);
          const forcedLang = /[\u0900-\u097F]/.test(cleanText) ? 'hi' : sessionLang;
          tts.play(cleanText, {
            language: forcedLang,
            speed: sessionSpeed,
            voice_gender: gender,
            withTimestamps: true,
            onWordChange: setActiveWordIdx,
            // Iter-52.15 — for library passages, enable the shared TTS
            // cache so the FIRST reader anywhere pays the ElevenLabs
            // call and every subsequent reader replays for free.
            library_passage_id: book?.is_library ? book.book_id : undefined,
          });
        }}
          disabled={tts.loading || !hasAttempted}
          title={!hasAttempted ? (sessionLang === 'hi' ? 'पहले खुद पढ़ने की कोशिश करो' : 'Try reading first — tap the mic above') : ''}
          className={`w-full font-bold px-4 py-2 rounded-full text-sm inline-flex items-center justify-center gap-1.5 ${hasAttempted ? 'bg-[#5B8DEF] text-white' : 'bg-slate-200 text-slate-500 cursor-not-allowed'}`}>
          {tts.loading ? <Loader2 className="animate-spin" size={16}/> : <Volume2 size={16} strokeWidth={3}/>}{' '}
          {hasAttempted
            ? t('readToMe')
            : (sessionLang === 'hi'
                ? <span data-testid="read-to-me-locked" className="font-hi">पहले पढ़ो</span>
                : <span data-testid="read-to-me-locked">Try reading first</span>)}
        </button>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          GROUP 2 · UNDERSTAND — comprehension helpers.
          Three uniform pills of equal size / equal padding, one row.
          Kept in one shared "Understand" strip so parents can find all
          three helpers at a glance instead of scanning a wall of pills.
      ────────────────────────────────────────────────────────────── */}
      <div data-testid="understand-group" className="flex flex-wrap items-center justify-center gap-2 mb-3 max-w-md mx-auto">
        <button data-testid="meaning-btn" onClick={loadMeaning} disabled={meaningLoading}
          className="flex-1 min-w-[100px] bg-[#5B8DEF] text-white font-bold px-3 py-2 rounded-full text-xs inline-flex items-center justify-center gap-1 disabled:opacity-60">
          {meaningLoading ? <Loader2 size={14} className="animate-spin"/> : <Languages size={14} strokeWidth={3}/>} {t('meaning')}
        </button>
        {/* Iter-51.2: two-language summary buttons — parents may want the
            English side for kids and Hindi for themselves, regardless of
            what the source chunk is written in. */}
        <button data-testid="summarize-en-btn" onClick={() => loadSummary('en')} disabled={summaryLoading}
          className="flex-1 min-w-[100px] bg-[#B85582] text-white font-bold px-3 py-2 rounded-full text-xs inline-flex items-center justify-center gap-1 disabled:opacity-60">
          {summaryLoading ? <Loader2 size={14} className="animate-spin"/> : <Sparkles size={14} strokeWidth={3}/>} Summary EN
        </button>
        <button data-testid="summarize-hi-btn" onClick={() => loadSummary('hi')} disabled={summaryLoading}
          className="flex-1 min-w-[100px] bg-[#A76A00] text-white font-bold px-3 py-2 rounded-full text-xs inline-flex items-center justify-center gap-1 font-hi disabled:opacity-60">
          {summaryLoading ? <Loader2 size={14} className="animate-spin"/> : <Sparkles size={14} strokeWidth={3}/>} सारांश हिंदी
        </button>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          GROUP 3 · CHALLENGE — Beat the Bot.
          Upgraded from a small green pill to a gradient banner that
          shares the orange→amber DNA of the Home / Books hero cards
          — so this feature reads as the SAME feature everywhere it
          appears in the app.
      ────────────────────────────────────────────────────────────── */}
      <button data-testid="beat-the-bot-btn"
        onClick={() => {
          const text = (currentChunk || '').trim();
          if (!text || text.length < 40) {
            toast.error('Read this chunk first — the quiz needs a bit more text.');
            return;
          }
          // Iter-51.22 — pass all pages so the Quiz screen can offer
          // "last 2 / last 3 / whole book" scope options. Each page
          // ships as {page_id, text} so the Quiz can rebuild the
          // scope on-the-fly.
          const allPages = (book.pages || []).map(p => ({
            page_id: p.page_id,
            text: (p.text || '').replace(/^##\s*/gm, '').trim(),
          }));
          nav(`/quiz/${bookId}`, {
            state: {
              text,
              language: sessionLang,
              cacheKey: `${bookId}:p${pageIdx}:c${safeChunkIdx}`,
              pageIdx,
              chunkIdx: safeChunkIdx,
              pages: allPages,
              currentChunkText: text,
              bookTitle: book?.title || '',
            },
          });
        }}
        className="w-full max-w-md mx-auto flex items-center justify-center gap-2 rounded-2xl py-3 mb-4 text-white font-extrabold text-sm relative overflow-hidden transition-transform hover:scale-[1.01] active:scale-[0.99]"
        style={{
          background: 'linear-gradient(135deg, #FF6B1A 0%, #F4B740 100%)',
          boxShadow: '0 8px 20px -10px rgba(255,107,26,0.55)',
        }}>
        <BotIcon size={18} strokeWidth={3}/>
        <span>Beat the Bot</span>
        <ChevronRight size={16} strokeWidth={3.5} className="opacity-90"/>
      </button>

      {/* Iter-52.52 — Reading page stays focused on the passage; the
          full exercises + Answer Key live on their own /exercises
          screen. When the current page contains any back-of-lesson
          questions (detected via /exercises/detect on ExercisesScreen)
          the CTA takes the child there. We pre-hint the count via a
          lightweight detect ping so the badge shows N — falls back
          to "Open" when the count isn't yet available. */}
      {book?.pages?.[pageIdx] && (() => {
        const rawText = book.pages[pageIdx].text || '';
        const parsedMatch = parseMatchExercise(rawText);
        const cleanPassage = stripMarkers(rawText).replace(/^##\s*/gm, '').trim();
        if ((!cleanPassage || cleanPassage.length < 60) && !parsedMatch.isMatch) return null;
        return (
          <ExercisesCta
            bookId={bookId}
            pageId={book.pages[pageIdx].page_id}
            passage={cleanPassage}
            language={sessionLang}
            matchBlock={parsedMatch.isMatch ? {
              instruction: parsedMatch.instruction,
              left: parsedMatch.left,
              right: parsedMatch.right,
              left_images: parsedMatch.leftImages || [],
            } : null}
          />
        );
      })()}

      {meaningText && <div data-testid="meaning-text" className={`mb-3 p-3 bg-[#5B8DEF] rounded-2xl text-sm text-white break-words ${hiFont}`}>{meaningText}</div>}
      {summaryText && <div data-testid="summary-text" className={`mb-3 p-3 bg-[#D97398] rounded-2xl text-sm text-[#2B2140] break-words ${hiFont}`}>{summaryText}</div>}

      {coachWord && coachStage !== 'idle' && (
        <div data-testid="coach-panel" className="bg-[#D97398] rounded-2xl p-4 mb-4 flex items-center gap-3">
          <Wand2 className="text-[#1E9E6C]" size={22} strokeWidth={2.5}/>
          <div className="flex-1 min-w-0">
            <div className={`font-extrabold text-[#2B2140] break-words ${hiFont}`}>
              {coachStage === 'done' ? t('coachDone') : `${t('coachRepeat')} ${coachWord}`}
            </div>
          </div>
          {coachStage !== 'done' && (
            <button data-testid="coach-mic-btn" onClick={coachMic} className="bg-[#FF6B1A] text-white font-bold p-3 rounded-full">
              {coachStage === 'listening' ? <Loader2 className="animate-spin" size={20}/> : <Mic size={20} strokeWidth={3}/>}
            </button>
          )}
          <button data-testid="coach-close-btn" onClick={() => setCoachWord(null)} className="text-slate-400 text-lg font-bold">×</button>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          GROUP 4 · NAVIGATE — chunk size + chunk nav + page nav.
          All three sit in one shared white card so the "how far / how
          much" controls read as one cluster. Chunk-size first (sets
          the granularity), then Prev/Next chunk (moves within the
          page), then Prev/Next page (jumps a whole page — only shown
          on multi-page books).
      ────────────────────────────────────────────────────────────── */}
      <div data-testid="navigate-group" className="bg-white rounded-2xl border border-slate-200 p-3 mb-4 max-w-md mx-auto space-y-2">
        <div className="flex flex-wrap items-center gap-2 justify-center">
          <span className={`text-[10px] font-bold uppercase text-[#2B2140]/60 ${hiFont}`}>{t('chunkSize')}:</span>
          {['short','medium','long'].map(k => (
            <button data-testid={`chunk-size-${k}`} key={k} onClick={() => saveChunkPref(k)}
              className={`px-3 py-1.5 rounded-full font-bold text-xs border transition-colors ${chunkSize===k?'bg-[#FF6B1A] text-white border-[#FF6B1A]':'bg-white text-[#2B2140] border-slate-200 hover:border-[#FF6B1A]/40'}`}>{t(k)}</button>
          ))}
        </div>
        <div className="flex items-center justify-center gap-2">
          <button data-testid="prev-chunk-btn" onClick={goPrev} disabled={pageIdx===0 && safeChunkIdx===0}
            className="flex-1 bg-white border border-slate-200 text-[#2B2140] font-bold px-3 py-2 rounded-full inline-flex items-center justify-center gap-1 disabled:opacity-40 text-xs">
            <ChevronLeft size={16} strokeWidth={3}/> {t('prevChunk')}
          </button>
          <button data-testid="next-chunk-btn" onClick={goNext}
            className="flex-1 bg-white border border-slate-200 text-[#2B2140] font-bold px-3 py-2 rounded-full inline-flex items-center justify-center gap-1 text-xs">
            {t('nextChunk')} <ChevronRight size={16} strokeWidth={3}/>
          </button>
        </div>
        {book.pages.length > 1 && (
          <div className="flex items-center justify-center gap-2 pt-1 border-t border-slate-100">
            <button data-testid="prev-page-btn" onClick={goPrevPage} disabled={pageIdx === 0}
              className="flex-1 bg-[#2B2140] text-white font-bold px-3 py-2 rounded-full inline-flex items-center justify-center gap-1 disabled:opacity-40 text-xs">
              <ChevronLeft size={14} strokeWidth={3}/> Prev Page
            </button>
            <span className="text-[10px] font-extrabold text-slate-500 tabular-nums shrink-0">
              P {pageIdx + 1}/{book.pages.length}
            </span>
            <button data-testid="next-page-btn" onClick={goNextPage} disabled={pageIdx + 1 >= book.pages.length}
              className="flex-1 bg-[#2B2140] text-white font-bold px-3 py-2 rounded-full inline-flex items-center justify-center gap-1 disabled:opacity-40 text-xs">
              Next Page <ChevronRight size={14} strokeWidth={3}/>
            </button>
          </div>
        )}
      </div>

      {/* Iter-52.16b — dedicated purple "Finish this passage" CTA for
          Starter Library reads. Previously the finish signal was
          shoved into the same row as Prev/Next Chunk (kids missed
          it entirely). Now it sits ON ITS OWN below the chunk-nav
          block with a purple gradient + star icon so parents/kids
          understand it's the next step after finishing a passage.
          Rendered ONLY on the very last chunk of the last page of a
          library book. */}
      {book?.is_library
        && pageIdx + 1 >= book.pages.length
        && safeChunkIdx + 1 >= chunks.length && (
        <button
          ref={finishCtaRef}
          data-testid="library-finish-cta"
          onClick={goNext}
          className="w-full mt-3 mb-2 py-4 px-6 rounded-3xl text-white font-extrabold text-base inline-flex items-center justify-center gap-2 shadow-lg hover:brightness-110 active:brightness-95 transition-all"
          style={{
            background: 'linear-gradient(135deg, #6A4FCF 0%, #4C3B99 100%)',
            boxShadow: '0 14px 28px -12px rgba(76,59,153,0.55)',
          }}
        >
          <Sparkles size={20} strokeWidth={3}/>
          {sessionLang === 'hi'
            ? <span className="font-hi">पूरा पढ़ा — अब अपनी किताब आज़माओ</span>
            : <>Finish passage — try your own book next</>}
          <ChevronRight size={20} strokeWidth={3}/>
        </button>
      )}


      {result && (
        <div data-testid="reading-result" className="bg-[#F4B740] rounded-3xl p-5 mt-2">
          <div className="text-center mb-3">
            <Trophy size={36} strokeWidth={2.5} className="text-[#FF6B1A] mx-auto mb-2"/>
            <p data-testid="feedback-message" className={`text-lg font-bold text-[#2B2140] break-words ${hiFont}`}>{result.feedback}</p>
          </div>
          <div className="flex justify-center gap-3 mb-3 flex-wrap">
            <div className="bg-white rounded-2xl px-5 py-3">
              <div className="text-xs font-bold text-[#2B2140]/60 uppercase">{t('accuracy')}</div>
              <div className="text-2xl font-extrabold text-[#1E9E6C]">{result.accuracy}%</div>
            </div>
            <div className="bg-white rounded-2xl px-5 py-3 flex items-center gap-2">
              <Star size={22} className="text-[#FF6B1A] fill-[#FF6B1A]"/>
              <div>
                <div className="text-xs font-bold text-[#2B2140]/60 uppercase">{t('points')}</div>
                <div className="text-2xl font-extrabold text-[#2B2140]">+{result.points}</div>
              </div>
            </div>
          </div>
          <div className="flex justify-center gap-2 flex-wrap mb-3">
            {myAudioUrl && (
              <button data-testid="play-my-reading-btn" onClick={() => {
                // Iter-52.34 — silence ALL other audio sources before
                // starting the child's own recording. Without this,
                // parents heard the celebration voice (or the feedback
                // TTS) overlapping the child's playback because those
                // sources live on separate <audio> elements the raw
                // `new Audio(...).play()` below can't reach. See
                // /app/frontend/src/lib/milestones.js for the module-
                // level celebration audio ref stopCelebrationAudio()
                // uses.
                try { tts.stop(); } catch { /* noop */ }
                stopCelebrationAudio();
                new Audio(myAudioUrl).play().catch(() => {});
              }}
                className="bg-white text-[#2B2140] font-bold px-4 py-2 rounded-full inline-flex items-center gap-1 text-sm">
                <Play size={14} strokeWidth={3}/> {t('playMyReading')}
              </button>
            )}
            <button data-testid="play-correct-btn" onClick={() => speak(stripMarkers(currentChunk), { speed: 0.75 })}
              className="bg-[#5B8DEF] text-white font-bold px-4 py-2 rounded-full inline-flex items-center gap-1 text-sm">
              <Volume2 size={14} strokeWidth={3}/> {t('playCorrect')}
            </button>
          </div>
          {missedWords.length > 0 && (
            <div data-testid="practice-words" className="mt-1 bg-white/70 rounded-2xl p-3">
              <div className={`text-sm font-bold text-[#2B2140] mb-1 ${hiFont}`}>{t('practiceWords')}</div>
              <div className={`text-xs text-[#2B2140]/60 mb-2 ${hiFont}`}>{t('tapToHear')}</div>
              <div className="flex flex-wrap gap-2">
                {missedWords.map((w, i) => (
                  <button data-testid={`practice-word-${i}`} key={i} onClick={() => startCoach(w)}
                    className={`bg-[#F4B740] text-[#B45309] font-bold px-3 py-1.5 rounded-full inline-flex items-center gap-1 text-sm ${hiFont}`}>
                    <Volume2 size={12} strokeWidth={3}/> {w}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-center gap-2 mt-3 flex-wrap">
            <button data-testid="try-again-btn" onClick={() => { setResult(null); setMyAudioUrl(null); }} className="bg-white text-[#2B2140] font-bold px-4 py-2 rounded-full inline-flex items-center gap-1 text-sm">
              <RotateCcw size={14} strokeWidth={3}/> {t('tryAgain')}
            </button>
            {/* Iter-52.19 — Finish CTA is ALSO rendered here inside
                the result box on the last chunk of a library passage.
                Previously the only Finish button was the big purple
                bar BELOW the chunk-nav row, which parents missed on
                long passages (e.g. Independence Day, 9 chunks) —
                after scoring the last chunk they saw the result box
                + Try again and didn't scroll further down to find
                the CTA. This inline button ensures the handoff is
                one tap away from the "you finished a chunk" state
                without changing the underlying goNext logic. */}
            {book?.is_library
              && pageIdx + 1 >= book.pages.length
              && safeChunkIdx + 1 >= chunks.length && (
              <button
                data-testid="library-finish-btn-inline"
                onClick={goNext}
                className="bg-[#6A4FCF] text-white font-extrabold px-4 py-2 rounded-full inline-flex items-center gap-1.5 text-sm shadow-md hover:brightness-110"
              >
                <Sparkles size={14} strokeWidth={3}/>
                {sessionLang === 'hi'
                  ? <span className="font-hi">पूरा किया — अपनी किताब पर जाओ</span>
                  : <>Finish — go to my own book</>}
              </button>
            )}
          </div>
        </div>
      )}

      {debugOpen && (
        <div data-testid="scoring-debug-panel" className="mt-3 bg-[#1E1B2E] text-slate-100 rounded-2xl p-4 pb-8 font-mono text-[11px] leading-relaxed overflow-x-auto">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[#FFB84D] font-bold uppercase tracking-wider">🔍 Scoring debug</div>
            <button data-testid="debug-close-btn" onClick={() => { setDebugOpen(false); setDebugPin(''); }} className="text-slate-400 hover:text-white">×</button>
          </div>
          {/* Iter-51.57 — surface the Change Debug PIN card FIRST so
              parents can reach it without scrolling past long admin
              tables on mobile. */}
          <AdminChangePinCard debugPin={debugPin} onChanged={(np) => setDebugPin(np)} />
          {/* Moderation flags — most recent adult/racy/violence verdicts
              from Claude Vision moderation. Wired to /api/debug/moderation-flags
              which is gated by the same debug PIN we already have cached. */}
          <ModerationFlagsCard debugPin={debugPin} />
          {/* Admin controls (iter-49): user management + editable plans copy. */}
          <AdminUsersCard debugPin={debugPin} />
          <AdminPlansCard debugPin={debugPin} />
          <AdminTestimonialsCard debugPin={debugPin} />
          {/* Milestone tester — always visible so streak/reward/perfect can
              be QA'd even before the first chunk of the session is read. */}
          <MilestoneTester
            debugPin={debugPin}
            childId={activeChild?.child_id}
            language={sessionLang}
            voiceGender={activeChild?.voice_gender || 'female'}
          />
          {(lastTranscript || result) && (
            <>
          <div className="mb-2 mt-3"><span className="text-[#7ED8B8]">reference:</span> <span className="text-white break-words">{currentChunk}</span></div>
          <div className="mb-2"><span className="text-[#7ED8B8]">raw STT transcript:</span> <span className="text-white break-words">{lastTranscript || '(none yet)'}</span></div>
          {result?.debug && (
            <>
              <div className="mb-2"><span className="text-[#7ED8B8]">spoken tokens (normalized):</span> <span className="text-slate-300 break-words">[{(result.debug.spoken_tokens || []).map(t => `"${t}"`).join(', ')}]</span></div>
              <div className="mb-1 text-[#7ED8B8]">per-word:</div>
              <div className="border border-slate-700 rounded-lg overflow-hidden">
                <table className="w-full text-left">
                  <thead className="bg-slate-800 text-[#FFB84D]">
                    <tr>
                      <th className="px-2 py-1">target</th>
                      <th className="px-2 py-1">candidate</th>
                      <th className="px-2 py-1">ratio</th>
                      <th className="px-2 py-1">conf</th>
                      <th className="px-2 py-1">threshold</th>
                      <th className="px-2 py-1">verdict</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(result.debug.rows || []).map((r, i) => (
                      <tr key={i} className={r.matched ? 'bg-emerald-900/30' : 'bg-rose-900/30'}>
                        <td className="px-2 py-0.5">{r.target}</td>
                        <td className="px-2 py-0.5">{r.candidate || '—'}</td>
                        <td className="px-2 py-0.5">{r.ratio.toFixed(3)}</td>
                        <td className="px-2 py-0.5">{r.confidence != null ? r.confidence.toFixed(2) : '—'}</td>
                        <td className="px-2 py-0.5">{r.threshold}</td>
                        <td className={`px-2 py-0.5 font-bold ${r.matched ? 'text-emerald-400' : 'text-rose-400'}`}>{r.matched ? 'MATCH' : r.reason === 'length_reject' ? 'REJECT (length)' : r.reason === 'low_confidence' ? 'MISS (low conf)' : 'MISS'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
            </>
          )}
        </div>
      )}

      <div className="text-center mt-4">
        <button data-testid="done-for-today-btn" onClick={sayGoodbye}
          disabled={goodbyeState !== 'idle'}
          className="bg-[#F4B740] text-[#B45309] font-bold px-5 py-2 rounded-full inline-flex items-center gap-2 text-sm disabled:opacity-60">
          {goodbyeState === 'idle'
            ? <><LogOut size={16} strokeWidth={3}/> {t('doneForToday')}</>
            : <><Loader2 size={16} className="animate-spin"/> Saying bye…</>}
        </button>
      </div>

      {/* Iter-51.6: compact goodbye overlay — visible the instant the
          button is tapped so the child sees IMMEDIATE feedback instead
          of ~10 s of blocked audio with a frozen screen. */}
      {goodbyeState !== 'idle' && (
        <div data-testid="goodbye-overlay"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-5 space-y-3">
            <div className="flex items-center gap-2">
              <div className={`w-9 h-9 rounded-2xl flex items-center justify-center text-white
                ${goodbyeState === 'listening' ? 'bg-red-500 animate-pulse' : 'bg-[#F4B740]'}`}>
                {goodbyeState === 'listening' ? <Mic size={16} strokeWidth={2.8}/> : <LogOut size={16} strokeWidth={2.8}/>}
              </div>
              <div className="font-extrabold text-[#2B2140]">
                {goodbyeState === 'preparing' && 'Wrapping up…'}
                {goodbyeState === 'greeting' && 'Listen'}
                {goodbyeState === 'listening' && 'Say bye now!'}
                {goodbyeState === 'closing' && 'See you soon'}
              </div>
            </div>
            {goodbyeText && (
              <p data-testid="goodbye-overlay-text"
                className={`text-sm text-slate-700 leading-relaxed ${sessionLang === 'hi' ? 'font-hi' : ''}`}>
                {goodbyeText}
              </p>
            )}
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full bg-[#FF6B1A] transition-all duration-500
                ${goodbyeState === 'preparing' ? 'w-1/4' : goodbyeState === 'greeting' ? 'w-2/4' : goodbyeState === 'listening' ? 'w-3/4' : 'w-full'}`}/>
            </div>
          </div>
        </div>
      )}

      {debugPinAsking && (
        <DebugPinModal
          onUnlocked={(verifiedPin) => { setDebugPinAsking(false); setDebugPin(verifiedPin || ''); setDebugOpen(true); }}
          onCancel={() => setDebugPinAsking(false)}
        />
      )}

      {/* Iter-52.1 — Permanent, non-dismissable AI-disclaimer pinned to
          the BOTTOM of the viewport (above the BottomNav). Rendered
          last so its DOM order lines up with its visual position and
          screen readers announce it after page content. */}
      <AiDisclaimerBanner language={sessionLang} testid="reading-ai-disclaimer" />

      {/* Iter-52.15 — Starter Library handoff. When a child finishes
          a shared library passage, we intentionally do NOT dump them
          back to Dashboard silently. Instead, this card celebrates
          the win AND funnels them straight into scanning their own
          textbook page, which is the whole point of the Starter
          Library existing at all. */}
      {showLibraryHandoff && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          data-testid="library-handoff-modal">
          <div className="bg-white rounded-3xl max-w-sm w-full p-6 text-center space-y-4 shadow-2xl">
            <div className="text-4xl">🎉</div>
            <h3 className="text-xl font-extrabold text-[#2B2140]">
              {sessionLang === 'hi' ? 'बहुत बढ़िया!' : 'Great reading!'}
            </h3>
            <p className={`text-[#2B2140] ${sessionLang === 'hi' ? 'font-hi' : ''}`}>
              {sessionLang === 'hi'
                ? 'अब अपनी किताब से एक पन्ना कोशिश करो।'
                : 'Now try a page from your own book.'}
            </p>
            <button
              data-testid="library-handoff-scan-btn"
              onClick={() => { setShowLibraryHandoff(false); nav('/books/new'); }}
              className="w-full bg-[#FF6B1A] text-white font-extrabold px-4 py-3 rounded-full inline-flex items-center justify-center gap-2 shadow-md hover:brightness-105"
            >
              <Camera size={18} strokeWidth={3}/> {sessionLang === 'hi' ? 'फ़ोटो लो' : 'Scan a page'}
            </button>
            <button
              data-testid="library-handoff-skip-btn"
              onClick={() => { setShowLibraryHandoff(false); nav('/dashboard'); }}
              className="w-full text-sm text-slate-500 font-bold py-2"
            >{sessionLang === 'hi' ? 'बाद में' : 'Maybe later'}</button>
          </div>
        </div>
      )}
    </div>
  );
}
